import { useCallback, useEffect, useRef, useState } from 'react';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { Chip } from '@/common/styles/shared.styles';
import {
	CategoryScrollNext,
	CategoryScrollPrev,
	CategoryScrollWrap,
	CategoryStrip,
	LoungeTags,
} from './categoryScroll.styles';

const CategoryScroll = ({ tags, activeCategory, onCategoryChange, isLoading }) => {
	const t = useLanguageData('home');
	const stripRef = useRef(null);
	const [showPrev, setShowPrev] = useState(false);
	const [showNext, setShowNext] = useState(false);

	const updateScrollButtons = useCallback(() => {
		const strip = stripRef.current;
		if (!strip) {
			return;
		}

		const maxScroll = strip.scrollWidth - strip.clientWidth;
		const hasOverflow = maxScroll > 4;
		const isAtStart = strip.scrollLeft <= 4;
		const isAtEnd = strip.scrollLeft >= maxScroll - 4;

		setShowPrev(hasOverflow && !isAtStart);
		setShowNext(hasOverflow && !isAtEnd);
	}, []);

	useEffect(() => {
		updateScrollButtons();
		window.addEventListener('resize', updateScrollButtons);
		return () => window.removeEventListener('resize', updateScrollButtons);
	}, [updateScrollButtons, tags]);

	const scroll = (direction) => {
		const strip = stripRef.current;
		if (!strip) {
			return;
		}
		const amount = Math.max(160, Math.round(strip.clientWidth * 0.46));
		strip.scrollBy({ left: direction * amount, behavior: 'smooth' });
	};

	return (
		<LoungeTags aria-label={t.loungeCategories}>
			<CategoryScrollWrap $showPrevFade={showPrev} $showNextFade={showNext}>
				<CategoryScrollPrev
					type="button"
					$visible={showPrev}
					disabled={!showPrev}
					aria-label={t.previousCategories}
					onClick={() => scroll(-1)}
				>
					<svg viewBox="0 0 24 24" aria-hidden="true">
						<path
							d="m15 6-6 6 6 6"
							fill="none"
							stroke="currentColor"
							strokeWidth="2.2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</CategoryScrollPrev>
				<CategoryStrip
					ref={stripRef}
					aria-label={t.categoriesLabel}
					onScroll={updateScrollButtons}
				>
					<Chip
						type="button"
						$active={activeCategory === 'all'}
						onClick={() => onCategoryChange('all')}
					>
						{t.all}
					</Chip>
					{isLoading && tags.length === 0 ? (
						<Chip type="button" disabled>
							{t.loadingTags ?? t.all}
						</Chip>
					) : (
						tags.map((tag) => (
							<Chip
								key={tag._id}
								type="button"
								$active={activeCategory === tag._id}
								onClick={() => onCategoryChange(tag._id)}
							>
								{tag.label}
							</Chip>
						))
					)}
				</CategoryStrip>
				<CategoryScrollNext
					type="button"
					$visible={showNext}
					disabled={!showNext}
					aria-label={t.nextCategories}
					onClick={() => scroll(1)}
				>
					<svg viewBox="0 0 24 24" aria-hidden="true">
						<path
							d="m9 6 6 6-6 6"
							fill="none"
							stroke="currentColor"
							strokeWidth="2.2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</CategoryScrollNext>
			</CategoryScrollWrap>
		</LoungeTags>
	);
};

export default CategoryScroll;
